import { store } from '../../state/patientProductionStore';

export function generateRefId(prefix: string): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

export function formatDate(d: Date): string {
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatPrice(n: number): string {
  return n.toLocaleString('en-IN');
}

// `generateSlots` lived here and drew a 09:00–18:00 grid with every time
// marked available, for any provider, on any date. Real availability comes
// from `GET /providers/:idOrSlug/slots` — see `useProviderSlots`, which also
// exports `preferredTimeGrid()` for providers that publish no schedule.

// onXConfirmed helpers mirror a successful booking into the local store.
// Pass `id` (the server id) whenever the API create succeeded — the store
// copy then dedupes against the API copy on pages that merge both sources.
export function onDoctorBookingConfirmed(data: {
  id?: string;
  doctorName: string;
  specialty: string;
  hospital: string;
  date: string;
  time: string;
  type: 'In-Person' | 'Telemedicine';
  notes?: string;
}) {
  return store.createAppointment({
    id: data.id,
    doctorName: data.doctorName,
    specialty: data.specialty,
    hospital: data.hospital || 'Clinic',
    date: data.date,
    time: data.time,
    type: data.type,
    notes: data.notes,
  });
}

export function onLabBookingConfirmed(data: {
  id?: string;
  testName: string;
  labName: string;
  date: string;
  time: string;
  collectionType: 'Walk-in' | 'Home Collection';
}) {
  return store.createLabOrder({
    id: data.id,
    testName: data.testName,
    labName: data.labName,
    date: data.date,
    time: data.time,
    collectionType: data.collectionType,
  });
}

// `id` is the server appointment id (imaging bookings POST /appointments).

export function onPharmacyOrderConfirmed(data: {
  id?: string;
  medicines: { name: string; qty: number; dosage: string; unitPrice?: number }[];
  pharmacyName: string;
  totalAmount: number;
  prescriptionId?: string;
  deliveryFee?: number;
  discount?: number;
  paymentMethod?: 'UPI' | 'CARD' | 'NET_BANKING' | 'COD' | 'WALLET' | 'INSURANCE';
  paymentStatus?: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  deliveryMode?: 'HOME' | 'PICKUP';
  deliveryAddress?: string;
}) {
  return store.createPharmacyOrder({
    id: data.id,
    medicines: data.medicines,
    pharmacyName: data.pharmacyName,
    totalAmount: data.totalAmount,
    prescriptionId: data.prescriptionId,
    deliveryFee: data.deliveryFee,
    discount: data.discount,
    paymentMethod: data.paymentMethod,
    paymentStatus: data.paymentStatus,
    deliveryMode: data.deliveryMode,
    deliveryAddress: data.deliveryAddress,
  });
}

// Only call AFTER the backend accepted the request — `id` is the server's
// requestId so the local mirror carries a reference dispatch can look up.
export function onAmbulanceRequested(data: {
  id?: string;
  emergencyType: string;
  ambulanceType?: string;
  location: string;
  destination?: string;
  notes?: string;
}) {
  return store.createAmbulanceRequest({
    id: data.id,
    emergencyType: data.emergencyType,
    ambulanceType: data.ambulanceType,
    location: data.location,
    destination: data.destination,
    notes: data.notes,
  });
}

// Only call AFTER the backend accepted the claim — `id` is the server's id.
export function onInsuranceClaimCreated(data: {
  id?: string;
  policyNumber: string;
  type: 'Claim' | 'Preauth';
  amount: number;
  description: string;
  hospital?: string;
  serviceType?: string;
  documents: string[];
}) {
  return store.createInsuranceClaim({
    id: data.id,
    policyNumber: data.policyNumber,
    type: data.type,
    amount: data.amount,
    description: data.description,
    hospital: data.hospital,
    serviceType: data.serviceType,
    documents: data.documents,
  });
}

export function onHospitalOpdConfirmed(data: {
  id?: string;
  doctorName: string;
  specialty: string;
  hospital: string;
  date: string;
  time: string;
  notes?: string;
}) {
  return store.createAppointment({
    id: data.id,
    doctorName: data.doctorName || 'OPD Doctor',
    specialty: data.specialty || 'General',
    hospital: data.hospital,
    date: data.date,
    time: data.time,
    type: 'In-Person',
    notes: data.notes,
  });
}
